import { useState, useRef } from 'react';
import { X, Upload, FileUp, AlertCircle, CheckCircle } from 'lucide-react';
import { importFormats, ImportFormat } from '../lib/importFormats';

interface ImportModalProps {
  onImport: (colors: string[], name?: string) => void;
  onClose: () => void;
}

export function ImportModal({ onImport, onClose }: ImportModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string[]>([]);
  const [detectedFormat, setDetectedFormat] = useState<ImportFormat | null>(null);
  const [paletteName, setPaletteName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const supportedFormats = [
    { ext: '.ase', label: 'Adobe Swatch Exchange', desc: 'Binary format from Adobe apps' },
    { ext: '.aco', label: 'Adobe Color', desc: 'Photoshop color swatch file' },
    { ext: '.gpl', label: 'GIMP Palette', desc: 'Text-based palette format' },
    { ext: '.css, .scss', label: 'CSS/SCSS Variables', desc: 'CSS custom properties or SCSS variables' },
    { ext: '.json', label: 'JSON', desc: 'JSON array or object with colors' },
    { ext: '.js, .ts', label: 'Tailwind Config', desc: 'Tailwind theme configuration' },
  ];

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setError(null);
    setPreview([]);
    setDetectedFormat(null);
    setPaletteName('');

    // Auto-preview
    await handlePreview(file);
  };

  const handlePreview = async (file: File) => {
    setImporting(true);
    setError(null);

    try {
      const result = await importFormats.importFromFile(file);

      if (result.error) {
        setError(result.error);
        setPreview([]);
        setDetectedFormat(null);
      } else {
        setPreview(result.colors);
        setPaletteName(result.name || file.name.replace(/\.[^.]+$/, ''));
        
        // Detect format for display
        const isBinary = file.name.toLowerCase().endsWith('.ase') || file.name.toLowerCase().endsWith('.aco');
        if (isBinary) {
          const arrayBuffer = await file.arrayBuffer();
          setDetectedFormat(importFormats.detectFormat(file.name, arrayBuffer));
        } else {
          const text = await file.text();
          setDetectedFormat(importFormats.detectFormat(file.name, text));
        }
      }
    } catch (err) {
      setError(`Failed to preview file: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setPreview([]);
      setDetectedFormat(null);
    } finally {
      setImporting(false);
    }
  };

  const handleImport = () => {
    if (preview.length === 0) {
      setError('No colors to import');
      return;
    }

    onImport(preview, paletteName || undefined);
    onClose();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
      handlePreview(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Import Palette</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto space-y-6">
          {/* File Upload Area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-blue-500 dark:hover:border-blue-400 transition-colors cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".ase,.aco,.gpl,.css,.scss,.json,.js,.ts"
              onChange={handleFileSelect}
              className="hidden"
            />
            <FileUp className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              Drop a palette file here or click to browse
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Supports ASE, ACO, GPL, CSS, JSON, and Tailwind config files
            </p>
          </div>

          {/* Selected File Info */}
          {selectedFile && (
            <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <Upload className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                      {detectedFormat && detectedFormat !== 'auto' && (
                        <span className="ml-2">• Format: {detectedFormat.toUpperCase()}</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/40 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-900 dark:text-red-100">Import Error</p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Preview */}
          {preview.length > 0 && (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-500/40 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-green-900 dark:text-green-100">
                    Successfully parsed {preview.length} color{preview.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                    Preview the colors below and click Import to add them to your palette.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Palette Name (optional)
                </label>
                <input
                  type="text"
                  value={paletteName}
                  onChange={(e) => setPaletteName(e.target.value)}
                  placeholder="Enter palette name"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Color Preview
                </label>
                <div className="grid grid-cols-8 gap-2">
                  {preview.map((color, index) => (
                    <div key={index} className="space-y-1">
                      <div
                        className="w-full aspect-square rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                      <p className="text-xs text-center text-gray-600 dark:text-gray-400 font-mono truncate">
                        {color}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Supported Formats */}
          {!selectedFile && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                Supported Formats
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {supportedFormats.map((format, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                  >
                    <p className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                      {format.label}
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {format.ext}
                      </span>
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{format.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={preview.length === 0 || importing}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Import {preview.length > 0 && `(${preview.length} colors)`}
          </button>
        </div>
      </div>
    </div>
  );
}
